import { useEffect, useState, useRef } from 'react';

// ============================================================
// NEUROLONIC ESP32-C3 RING — BLE AUDIO HANDLER
// ============================================================
// Connects to the ESP32-C3 field recorder via Bluetooth and
// automatically fetches all WAV files from the SD card using
// the LIST / GET / DONE protocol. After the queue is empty it
// re-polls every 30 seconds so new recordings are processed
// without needing to re-pair.
//
// BLE Protocol (firmware: ESP32C3_AudioRec):
//   Web → ESP32 via COMMAND char (WRITE):
//     "LIST"         → JSON on AUDIO char: {"files":[{name,size},...]}
//     "GET:filename" → 4-byte uint32 file size (LE)
//                      N × data packets (≤509 bytes each)
//                      4-byte ASCII "DONE" sentinel
//   File is deleted from SD after DONE is received.
// ============================================================

const SERVICE_UUID      = '12345678-1234-1234-1234-123456789abc';
const CHAR_UUID_AUDIO   = '12345678-1234-1234-1234-123456789abd'; // NOTIFY
const CHAR_UUID_COMMAND = '12345678-1234-1234-1234-123456789abe'; // WRITE

const PREDICT_BASE = process.env.REACT_APP_PREDICT_URL ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:5000'
    : 'https://app.neurolonic.com/app');

const STALL_MS      = 8000;  // ms without a packet before retry
const POLL_INTERVAL = 30000; // ms between automatic re-polls

function isDoneSentinel(data) {
  return data.length === 4 &&
    data[0] === 0x44 && data[1] === 0x4F &&
    data[2] === 0x4E && data[3] === 0x45; // "DONE"
}

// bleConnectKey is incremented by Home.jsx on every new pairing.
// This guarantees the effect re-runs even when the browser returns
// the same BluetoothDevice object reference for the same ring.
function RingAudioHandler({ userId, bleDevice, bleConnectKey, onAudioReceived, onError }) {
  // ---- UI state (synced from refs by 200ms polling interval) ----
  const [uiState, setUiState] = useState({
    status:    'connecting',
    progress:  0,
    autoSaved: false,
    queueText: '',
  });

  // ---- BLE characteristics ----
  const audioCharRef   = useRef(null);
  const commandCharRef = useRef(null);

  // ---- Transfer state (refs — never stale in BLE callbacks) ----
  const rxStateRef    = useRef('idle');
  const rxExpectedRef = useRef(0);
  const rxReceivedRef = useRef(0);
  const rxChunksRef   = useRef([]);
  const fileQueueRef  = useRef([]);
  const queueIndexRef = useRef(0);
  const lastPacketRef = useRef(0);
  const stallTimerRef = useRef(null);
  const pollTimerRef  = useRef(null);
  const uploadingRef  = useRef(false);

  // ---- UI state refs (synced to React state by polling interval) ----
  const statusRef    = useRef('connecting');
  const progressRef  = useRef(0);
  const autoSavedRef = useRef(false);
  const queueTextRef = useRef('');

  // ---- Prop refs ----
  const userIdRef          = useRef(userId);
  const onAudioReceivedRef = useRef(onAudioReceived);
  const onErrorRef         = useRef(onError);
  useEffect(() => { userIdRef.current = userId; },                      [userId]);
  useEffect(() => { onAudioReceivedRef.current = onAudioReceived; }, [onAudioReceived]);
  useEffect(() => { onErrorRef.current = onError; },                  [onError]);

  // ---- 200ms polling: syncs refs → React state ----
  useEffect(() => {
    const id = setInterval(() => {
      setUiState({
        status:    statusRef.current,
        progress:  progressRef.current,
        autoSaved: autoSavedRef.current,
        queueText: queueTextRef.current,
      });
    }, 200);
    return () => clearInterval(id);
  }, []);

  // ---- Main BLE effect — re-runs on every new pairing ----
  useEffect(() => {
    if (!bleDevice) return;
    let mounted = true;

    const setStatus = (s) => { statusRef.current = s; };

    // -- Stall watchdog --
    const clearStall = () => {
      if (stallTimerRef.current) { clearInterval(stallTimerRef.current); stallTimerRef.current = null; }
    };
    const startStall = () => {
      clearStall();
      lastPacketRef.current = Date.now();
      stallTimerRef.current = setInterval(() => {
        if (rxStateRef.current !== 'data') { clearStall(); return; }
        if (Date.now() - lastPacketRef.current > STALL_MS) {
          console.warn('[BLE Ring] Transfer stalled — retrying');
          clearStall();
          rxStateRef.current    = 'idle';
          rxChunksRef.current   = [];
          rxReceivedRef.current = 0;
          setTimeout(() => requestNextFile(), 800);
        }
      }, 1000);
    };

    // -- 30-second auto-poll after queue is exhausted --
    const clearPoll = () => {
      if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; }
    };
    const scheduleNextPoll = () => {
      clearPoll();
      pollTimerRef.current = setTimeout(async () => {
        if (!mounted) return;
        console.log('[BLE Ring] Auto-poll: checking for new recordings...');
        fileQueueRef.current  = [];
        queueIndexRef.current = 0;
        setStatus('fetching');
        queueTextRef.current = '';
        await sendCommand('LIST');
      }, POLL_INTERVAL);
    };

    // -- Send a text command to the ESP32 --
    const sendCommand = async (cmd) => {
      if (!commandCharRef.current) return;
      await commandCharRef.current.writeValue(new TextEncoder().encode(cmd));
    };

    // -- Request the next file in the queue (or schedule re-poll if done) --
    const requestNextFile = async () => {
      if (!mounted) return;
      const queue = fileQueueRef.current;
      const idx   = queueIndexRef.current;
      if (idx >= queue.length) {
        // Queue exhausted — idle and schedule the next LIST in 30s
        setStatus('idle');
        queueTextRef.current = '';
        scheduleNextPoll();
        return;
      }
      const filename = queue[idx].name;
      queueTextRef.current = `${filename} (${idx + 1}/${queue.length})`;
      setStatus('transferring');
      progressRef.current = 0;
      console.log('[BLE Ring] GET:', filename);
      await sendCommand('GET:' + filename);
    };

    // -- Upload a completed WAV blob to /predict --
    const uploadBlob = async (blob, filename) => {
      if (!mounted) return;
      uploadingRef.current = true;
      setStatus('uploading');
      try {
        const form = new FormData();
        form.append('file', blob, filename);
        form.append('user_id', String(userIdRef.current));
        form.append('auto_save', 'true');
        console.log('[BLE Ring] POST /predict |', filename, '|', blob.size, 'bytes');
        const res = await fetch(`${PREDICT_BASE}/predict`, { method: 'POST', body: form });
        if (!res.ok) throw new Error('HTTP ' + res.status + ': ' + res.statusText);
        const result = await res.json();
        console.log('[BLE Ring] Predict result:', result);
        autoSavedRef.current = !!result.auto_saved;
        setStatus('done');
        if (onAudioReceivedRef.current) onAudioReceivedRef.current(result);
        setTimeout(() => { if (statusRef.current === 'done') setStatus('idle'); }, 3000);
      } catch (err) {
        console.error('[BLE Ring] Upload error:', err);
        setStatus('error');
        if (onErrorRef.current) onErrorRef.current(err.message);
        setTimeout(() => { if (statusRef.current === 'error') setStatus('idle'); }, 3000);
      } finally {
        uploadingRef.current = false;
      }
    };

    // -- BLE notification handler --
    const onBLEData = (event) => {
      if (!mounted) return;
      const data = new Uint8Array(event.target.value.buffer);

      // ── IDLE: waiting for LIST JSON or size packet ──────────────────
      if (rxStateRef.current === 'idle') {
        const text = new TextDecoder().decode(data).trim();

        if (text.startsWith('{')) {
          // LIST response — parse file queue and start fetching
          try {
            const json  = JSON.parse(text);
            const files = (json.files || []).map(f =>
              typeof f === 'string' ? { name: f, size: 0 } : { name: f.name, size: f.size }
            );
            console.log('[BLE Ring] LIST:', files);
            fileQueueRef.current  = files;
            queueIndexRef.current = 0;

            if (files.length === 0) {
              console.log('[BLE Ring] No new files on SD card — will re-poll in 30s');
              setStatus('idle');
              queueTextRef.current = 'No new recordings';
              scheduleNextPoll();
              return;
            }
            requestNextFile();
          } catch (e) {
            console.error('[BLE Ring] LIST parse error:', e, text);
          }
          return;
        }

        // Size packet: 4 bytes, not "DONE"
        if (data.length === 4 && !isDoneSentinel(data)) {
          rxExpectedRef.current  = new DataView(data.buffer).getUint32(0, true);
          rxReceivedRef.current  = 0;
          rxChunksRef.current    = [];
          rxStateRef.current     = 'data';
          console.log('[BLE Ring] Size packet:', rxExpectedRef.current, 'bytes');
          startStall();
          return;
        }
        return;
      }

      // ── DATA: accumulating file bytes, watching for DONE ────────────
      if (rxStateRef.current === 'data') {
        if (isDoneSentinel(data)) {
          clearStall();
          const fname  = fileQueueRef.current[queueIndexRef.current]?.name || 'recording.wav';
          const rcvd   = rxReceivedRef.current;
          const chunks = rxChunksRef.current;
          console.log('[BLE Ring] DONE for', fname, '—', rcvd, 'bytes');

          // Reset receive state
          rxStateRef.current    = 'idle';
          rxChunksRef.current   = [];
          rxReceivedRef.current = 0;
          queueIndexRef.current++;

          if (rcvd > 44) {
            // Assemble → upload → then fetch next
            const merged = new Uint8Array(rcvd);
            let off = 0;
            for (const c of chunks) { merged.set(c, off); off += c.length; }
            const blob = new Blob([merged], { type: 'audio/wav' });
            uploadBlob(blob, fname).then(() => {
              if (mounted) setTimeout(() => requestNextFile(), 500);
            });
          } else {
            // Stub / empty file — skip, move to next
            if (mounted) setTimeout(() => requestNextFile(), 200);
          }
          return;
        }

        // Regular data — accumulate
        rxChunksRef.current.push(new Uint8Array(data));
        rxReceivedRef.current += data.length;
        lastPacketRef.current  = Date.now();
        progressRef.current    = rxExpectedRef.current > 0
          ? Math.min(100, Math.round((rxReceivedRef.current / rxExpectedRef.current) * 100))
          : 0;
      }
    };

    // -- Connect and kick off LIST --
    const connect = async () => {
      try {
        console.log('[BLE Ring] Connecting (key=' + bleConnectKey + ')...');
        setStatus('connecting');

        // If already connected reuse the server, otherwise connect fresh
        const server = bleDevice.gatt.connected
          ? bleDevice.gatt
          : await bleDevice.gatt.connect();

        if (!mounted) return;

        const service = await server.getPrimaryService(SERVICE_UUID);
        audioCharRef.current   = await service.getCharacteristic(CHAR_UUID_AUDIO);
        commandCharRef.current = await service.getCharacteristic(CHAR_UUID_COMMAND);

        // Stop any previous notifications before re-starting (avoids "already started" throws)
        try { await audioCharRef.current.stopNotifications(); } catch (_) {}
        await audioCharRef.current.startNotifications();
        audioCharRef.current.addEventListener('characteristicvaluechanged', onBLEData);

        bleDevice.addEventListener('gattserverdisconnected', () => {
          if (mounted) {
            console.log('[BLE Ring] GATT disconnected');
            setStatus('disconnected');
            clearStall();
            clearPoll();
          }
        });

        console.log('[BLE Ring] Connected. Sending LIST...');
        setStatus('fetching');
        await sendCommand('LIST');

      } catch (err) {
        if (!mounted) return;
        console.error('[BLE Ring] Connect error:', err);
        setStatus('error');
        if (onErrorRef.current) onErrorRef.current(err.message);
      }
    };

    connect();

    return () => {
      mounted = false;
      clearStall();
      clearPoll();
      if (audioCharRef.current) {
        try { audioCharRef.current.removeEventListener('characteristicvaluechanged', onBLEData); } catch (_) {}
        try { audioCharRef.current.stopNotifications(); } catch (_) {}
        audioCharRef.current = null;
      }
      commandCharRef.current = null;
      // Disconnect GATT so the next pairing always starts fresh
      if (bleDevice?.gatt?.connected) {
        try { bleDevice.gatt.disconnect(); } catch (_) {}
      }
    };
  }, [bleDevice, bleConnectKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Status widget ----
  const { status, progress, autoSaved, queueText } = uiState;

  const dotColor =
    status === 'transferring' ? '#ff9800' :
    status === 'uploading'    ? '#2196f3' :
    status === 'done'         ? '#4caf50' :
    status === 'error'        ? '#f44336' :
    status === 'disconnected' ? '#777'    :
    status === 'fetching'     ? '#00bcd4' : '#4caf50';

  const isAnimated = ['connecting', 'fetching', 'transferring', 'uploading'].includes(status);

  const label =
    status === 'connecting'   ? 'Pi Ring — Connecting...'          :
    status === 'fetching'     ? 'Pi Ring — Checking for files...'  :
    status === 'transferring' ? `Pi Ring — Receiving ${queueText}` :
    status === 'uploading'    ? 'Pi Ring — Analysing...'           :
    status === 'done'         ? (autoSaved ? 'Pi Ring — Saved! ✓' : 'Pi Ring — Done') :
    status === 'error'        ? 'Pi Ring — Error (see console)'    :
    status === 'disconnected' ? 'Pi Ring — Disconnected'           :
    queueText === 'No new recordings'
      ? 'Pi Ring — No new recordings (checking in 30s)'
      : 'Pi Ring — Ready';

  return (
    <>
      <div style={{
        position: 'fixed', bottom: 20, right: 20,
        background: 'rgba(0,0,0,0.88)', color: 'white',
        padding: '0.75rem 1.25rem', borderRadius: 12,
        zIndex: 9998, minWidth: 270, fontSize: '0.9rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
      }}>
        {/* Status row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: status === 'transferring' ? 8 : 0 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            background: dotColor,
            animation: isAnimated ? 'rah-pulse 1s infinite' : 'none'
          }} />
          <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{label}</span>
        </div>

        {/* Transfer progress bar */}
        {status === 'transferring' && (
          <>
            <div style={{ width: '100%', height: 6, background: 'rgba(255,255,255,0.2)', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
              <div style={{ width: `${progress}%`, height: '100%', background: '#ff9800', transition: 'width 0.2s ease' }} />
            </div>
            <div style={{ fontSize: '0.82rem', opacity: 0.8 }}>{progress}% received</div>
          </>
        )}

        {status === 'uploading'    && <div style={{ fontSize: '0.82rem', opacity: 0.8, marginTop: 4 }}>Predicting emotions &amp; saving to dashboard...</div>}
        {status === 'error'        && <div style={{ fontSize: '0.82rem', opacity: 0.8, marginTop: 4 }}>Check console. Re-pair the ring if needed.</div>}
        {status === 'disconnected' && <div style={{ fontSize: '0.82rem', opacity: 0.8, marginTop: 4 }}>BLE connection lost.</div>}
      </div>

      <style>{`@keyframes rah-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }`}</style>
    </>
  );
}

export default RingAudioHandler;
